import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { Editor, EditorState, RichUtils, getDefaultKeyBinding, KeyBindingUtil, CompositeDecorator, getVisibleSelectionRect, AtomicBlockUtils } from 'draft-js';
import 'draft-js/dist/Draft.css';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import CodeIcon from '@mui/icons-material/Code';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import GifIcon from '@mui/icons-material/Gif';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';

// Style button component for the toolbar
const StyleButton = ({ active, label, onToggle, style, icon: Icon }) => {
  const handleToggle = (e) => {
    e.preventDefault();
    onToggle(style);
  };

  return (
    <ToolbarButton $active={active} onMouseDown={handleToggle}>
      {Icon ? <Icon fontSize="small" /> : label}
    </ToolbarButton>
  );
};

const BLOCK_TYPES = [
  { label: 'H1', style: 'header-two', icon: LooksOneIcon },
  { label: 'H2', style: 'header-three', icon: LooksTwoIcon },
  { label: 'Quote', style: 'blockquote', icon: FormatQuoteIcon },
  { label: 'UL', style: 'unordered-list-item', icon: FormatListBulletedIcon },
  { label: 'OL', style: 'ordered-list-item', icon: FormatListNumberedIcon },
];

const INLINE_STYLES = [
  { label: 'Bold', style: 'BOLD', icon: FormatBoldIcon },
  { label: 'Italic', style: 'ITALIC', icon: FormatItalicIcon },
  { label: 'Underline', style: 'UNDERLINE', icon: FormatUnderlinedIcon },
  { label: 'Code', style: 'CODE', icon: CodeIcon },
];

const LinkPopup = ({ position, url, onChange, onSubmit, onClose }) => {
  return (
    <LinkPopupContainer style={{ top: position.top, left: position.left }}>
      <input
        type="text"
        value={url}
        onChange={onChange}
        placeholder="Enter URL..."
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSubmit();
          } else if (e.key === 'Escape') {
            onClose();
          }
        }}
        autoFocus
      />
    </LinkPopupContainer>
  );
};

const ImagePopup = ({ position, onImageSelect, onGiphySelect, onClose }) => {
  const [tab, setTab] = useState('url');
  const [imageUrl, setImageUrl] = useState('');
  const [giphySearch, setGiphySearch] = useState('');
  const [giphyResults, setGiphyResults] = useState([]);

  const handleImageUrlSubmit = (e) => {
    e?.preventDefault();
    if (imageUrl) {
      onImageSelect(imageUrl);
      onClose();
    }
  };

  const searchGiphy = async () => {
    try {
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/search?api_key=${process.env.REACT_APP_GIPHY_API_KEY}&q=${encodeURIComponent(
          giphySearch
        )}&limit=9`
      );
      const data = await response.json();
      setGiphyResults(data.data);
    } catch (error) {
      console.error('Error fetching Giphy:', error);
    }
  };

  return (
    <ImagePopupContainer style={{ position: 'absolute', top: position.top, left: position.left }}>
      <TabContainer>
        <Tab $active={tab === 'url'} onClick={() => setTab('url')}>Image URL</Tab>
        <Tab $active={tab === 'giphy'} onClick={() => setTab('giphy')}>Giphy</Tab>
      </TabContainer>
      
      {tab === 'url' && (
        <UrlSection>
          <SearchInput
            type="text"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="Enter image URL..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleImageUrlSubmit();
              }
            }}
          />
          <SearchButton onClick={handleImageUrlSubmit}>Add Image</SearchButton>
        </UrlSection>
      )}

      {tab === 'giphy' && (
        <GiphySection>
          <SearchInput
            type="text"
            value={giphySearch}
            onChange={(e) => setGiphySearch(e.target.value)}
            placeholder="Search Giphy..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                searchGiphy();
              }
            }}
          />
          <SearchButton onClick={searchGiphy}>Search</SearchButton>
          <GiphyResults>
            {giphyResults.map((gif) => (
              <GiphyItem
                key={gif.id}
                onClick={() => {
                  onGiphySelect(gif.images.fixed_height.url);
                  onClose();
                }}
              >
                <img src={gif.images.fixed_height_small.url} alt={gif.title} />
              </GiphyItem>
            ))}
          </GiphyResults>
        </GiphySection>
      )}
    </ImagePopupContainer>
  );
};

const Media = ({ contentState, block }) => {
  const entityKey = block.getEntityAt(0);
  if (!entityKey) {
    return null;
  }
  const entity = contentState.getEntity(entityKey);
  const { src } = entity.getData();
  const type = entity.getType();

  return (
    <MediaWrapper>
      <img src={src} alt="" style={{ maxWidth: '100%' }} />
    </MediaWrapper>
  );
};

export default function RichTextEditor({ placeholder = "Start typing...", onChange, initialState }) {
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPopupPosition, setLinkPopupPosition] = useState(null);
  const [imagePopupPosition, setImagePopupPosition] = useState(null);

  // Add image decorator
  const decorator = new CompositeDecorator([
    {
      strategy: (contentBlock, callback, contentState) => {
        contentBlock.findEntityRanges(
          (character) => {
            const entityKey = character.getEntity();
            return (
              entityKey !== null &&
              contentState.getEntity(entityKey).getType() === 'LINK'
            );
          },
          callback
        );
      },
      component: ({ contentState, entityKey, children }) => {
        const { url } = contentState.getEntity(entityKey).getData();
        return (
          <a 
            href={url}
            style={{ color: '#9333ea', textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault();
              window.open(url, '_blank');
            }}
          >
            {children}
          </a>
        );
      }
    }
  ]);

  // Initialize editor state with default empty state
  const [editorState, setEditorState] = useState(() => {
    if (initialState) {
      return initialState;
    }
    return EditorState.createEmpty(decorator);
  });

  // Update editor state when initialState prop changes
  useEffect(() => {
    if (initialState) {
      setEditorState(initialState);
    }
  }, [initialState]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.LinkPopup') && !e.target.closest('.ImagePopup') && !e.target.closest('.ToolbarButton')) {
        setLinkPopupPosition(null);
        setImagePopupPosition(null);
        setLinkUrl('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEditorChange = (newState) => {
    const selection = newState.getSelection();
    const content = newState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());
    
    // Ensure the selection and content state are valid
    if (block) {
      setEditorState(newState);
      onChange?.(newState);
    }
  };

  const promptForImage = (e) => {
    e.preventDefault();
    const editorRoot = document.querySelector('.DraftEditor-root');
    const editorRect = editorRoot.getBoundingClientRect();
    const toolbarHeight = document.querySelector('.Toolbar').offsetHeight;
    
    setImagePopupPosition({
      top: "0",
      right: "0"
    });
  };

  const addImage = (src) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'IMAGE',
      'IMMUTABLE',
      { src }
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(
      editorState,
      { currentContent: contentStateWithEntity }
    );
    
    handleEditorChange(AtomicBlockUtils.insertAtomicBlock(
      newEditorState,
      entityKey,
      ' '
    ));
  };

  const promptForLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      // Get the current link if it exists
      const contentState = editorState.getCurrentContent();
      const startKey = selection.getStartKey();
      const startOffset = selection.getStartOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
      
      if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        setLinkUrl(linkInstance.getData().url);
      } else {
        setLinkUrl('');
      }

      // Get selection bounds to position popup
      const selectionRect = getVisibleSelectionRect(window);
      if (selectionRect) {
        setLinkPopupPosition({
          top: selectionRect.bottom + window.scrollY + 5,
          left: selectionRect.left + window.scrollX
        });
      }
    }
  };

  const addLink = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!linkUrl) return;
    
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        'LINK',
        'MUTABLE',
        { url: linkUrl }
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(
        editorState,
        { currentContent: contentStateWithEntity }
      );
      handleEditorChange(RichUtils.toggleLink(newEditorState, selection, entityKey));
      setLinkPopupPosition(null);
      setLinkUrl('');
    }
  };

  const hasLink = () => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
    return linkKey !== null;
  };

  const blockRendererFn = (block) => {
    if (block.getType() === 'atomic') {
      return {
        component: Media,
        editable: false,
      };
    }
    return null;
  };

  const handleKeyCommand = (command, editorState) => {
    if (command === 'ADD_LINK') {
      promptForLink(new Event('mousedown'));
      return 'handled';
    }
    
    // Handle inline style commands
    if (command === 'BOLD' || command === 'ITALIC' || command === 'UNDERLINE' || command === 'CODE') {
      const newState = RichUtils.toggleInlineStyle(editorState, command);
      if (newState) {
        handleEditorChange(newState);
        return 'handled';
      }
    }

    // Handle other default commands (like delete, backspace, etc)
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const keyBindingFn = (e) => {
    if (KeyBindingUtil.hasCommandModifier(e)) {
      switch (e.keyCode) {
        // Cmd/Ctrl + B
        case 66:
          return 'BOLD';
        // Cmd/Ctrl + I
        case 73:
          return 'ITALIC';
        // Cmd/Ctrl + U
        case 85:
          return 'UNDERLINE';
        // Cmd/Ctrl + `
        case 192:
          return 'CODE';
        // Cmd/Ctrl + K
        case 75:
          return 'ADD_LINK';
        default:
          break;
      }
    }
    return getDefaultKeyBinding(e);
  };

  const toggleBlockType = (blockType) => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const toggleInlineStyle = (inlineStyle) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();
  const currentStyle = editorState.getCurrentInlineStyle();

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarGroup>
          {BLOCK_TYPES.map((type) => (
            <StyleButton
              key={type.style}
              active={type.style === blockType}
              label={type.label}
              onToggle={toggleBlockType}
              style={type.style}
              icon={type.icon}
            />
          ))}
          <ToolbarButton
            $active={hasLink()}
            onMouseDown={promptForLink}
          >
            <LinkIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            onMouseDown={promptForImage}
          >
            <ImageIcon fontSize="small" />
          </ToolbarButton>
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          {INLINE_STYLES.map((type) => (
            <StyleButton
              key={type.style}
              active={currentStyle.has(type.style)}
              label={type.label}
              onToggle={toggleInlineStyle}
              style={type.style}
              icon={type.icon}
            />
          ))}
        </ToolbarGroup>
      </Toolbar>
      <Editor
        key={`editor-${initialState ? 'with-content' : 'empty'}`}
        editorState={editorState}
        onChange={handleEditorChange}
        handleKeyCommand={handleKeyCommand}
        keyBindingFn={keyBindingFn}
        blockRendererFn={blockRendererFn}
        placeholder={placeholder}
        textAlignment="left"
        textDirectionality="LTR"
      />
      {linkPopupPosition && (
        <LinkPopup
          position={linkPopupPosition}
          url={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onSubmit={addLink}
          onClose={() => setLinkPopupPosition(null)}
        />
      )}
      {imagePopupPosition && (
        <ImagePopup
          position={imagePopupPosition}
          onImageSelect={addImage}
          onGiphySelect={addImage}
          onClose={() => setImagePopupPosition(null)}
        />
      )}
    </EditorContainer>
  );
}

const EditorContainer = styled.div.attrs({
  className: 'EditorContainer'
})`
  border: 1px solid #e5e5e5;
  border-radius: 2px;
  padding: 0;
  min-height: 200px;
  height: 100%;
  display: flex;
  flex-direction: column;
  direction: ltr;
  text-align: left;

  .DraftEditor-root {
    flex: 1;
    padding: 10px;
    font-size: 14px;
    line-height: 1.5;
    font-weight: normal;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    direction: ltr;
    text-align: left;
  }

  .public-DraftEditor-content {
    min-height: 100%;
    font-weight: normal;
    direction: ltr;
    text-align: left;
    unicode-bidi: bidi-override;
  }

  .public-DraftStyleDefault-block {
    margin: 0;
    font-weight: normal;
    min-height: 1.5em;
    position: relative;
    direction: ltr;
    text-align: left;
    unicode-bidi: bidi-override;
  }

  .public-DraftStyleDefault-ltr {
    direction: ltr;
    text-align: left;
    unicode-bidi: bidi-override;
  }

  .public-DraftEditor-editorContainer {
    height: 100%;
  }

  .public-DraftEditorPlaceholder-root {
    position: absolute;
    color: #999;
    pointer-events: none;
    z-index: 1;
    top: 10px;
    left: 10px;
  }

  .public-DraftEditorPlaceholder-inner {
    color: #999;
  }

  .DraftEditor-editorContainer > div {
    height: 100%;
  }

  .public-DraftStyleDefault-header-one {
    font-size: 24px;
    font-weight: bold;
    margin: 16px 0;
  }

  .public-DraftStyleDefault-header-two {
    font-size: 20px;
    font-weight: bold;
    margin: 14px 0;
  }

  .public-DraftStyleDefault-header-three {
    font-size: 16px;
    font-weight: bold;
    margin: 12px 0;
  }
  
  figure {
    margin: 5px 0;
  }

  .public-DraftStyleDefault-blockquote {
    border-left: 3px solid #e5e5e5;
    margin: 12px 0;
    padding-left: 12px;
    color: #666;
    font-style: italic;
    font-weight: normal;
  }

  .public-DraftStyleDefault-unorderedListItem,
  .public-DraftStyleDefault-orderedListItem {
    margin: 4px 0;
    font-weight: normal;
  }

  .public-DraftStyleDefault-ul,
  .public-DraftStyleDefault-ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  [data-contents="true"] {
    height: 100%;
  }

  [style*="font-weight: bold"] {
    font-weight: bold !important;
  }
  [style*="font-style: italic"] {
    font-style: italic;
  }
  [style*="text-decoration: underline"] {
    text-decoration: underline;
  }
  [style*="font-family: monospace"] {
    font-family: monospace;
  }
`;

const Toolbar = styled.div.attrs({
  className: 'Toolbar'
})`
  display: flex;
  padding: 10px;
  border-bottom: 1px solid #e5e5e5;
  gap: 10px;
  height: 30px !important;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
`;

const ToolbarGroup = styled.div.attrs({
  className: 'ToolbarGroup'
})`
  display: flex;
  gap: 5px;
`;

const ToolbarDivider = styled.div.attrs({
  className: 'ToolbarDivider'
})`
  width: 1px;
  height: 24px;
  background: #e5e5e5;
`;

const ToolbarButton = styled.button.attrs({
  className: 'ToolbarButton'
})`
  padding: 5px 10px;
  border: none;
  background: ${props => props.$active ? '#f3e8ff' : 'transparent'};
  color: ${props => props.$active ? '#9333ea' : '#666'};
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    background: ${props => props.$active ? '#f3e8ff' : '#f5f5f5'};
  }
`;

const LinkPopupContainer = styled.div.attrs({
  className: 'LinkPopup'
})`
  position: fixed;
  background: white;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  padding: 5px;

  input {
    border: none;
    outline: none;
    font-size: 13px;
    font-family: "PT Sans";
    min-width: 200px;
    padding: 5px;
  }
`;

const ImagePopupContainer = styled.div.attrs({
  className: 'ImagePopup'
})`
  position: fixed;
  background: white;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  padding: 15px;
  min-width: 260px;
`;

const TabContainer = styled.div.attrs({
  className: 'TabContainer'
})`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
`;

const Tab = styled.button.attrs({
  className: 'Tab'
})`
  padding: 5px 10px;
  border: none;
  background: ${props => props.$active ? '#f3e8ff' : 'transparent'};
  color: ${props => props.$active ? '#9333ea' : '#666'};
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    background: ${props => props.$active ? '#f3e8ff' : '#f5f5f5'};
  }
`;

const UrlSection = styled.div.attrs({
  className: 'UrlSection'
})`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SearchInput = styled.input.attrs({
  className: 'SearchInput'
})`
  padding: 5px;
  border: 1px solid #e5e5e5;
  border-radius: 3px;
  outline: none;
  font-size: 13px;
  font-family: "PT Sans";
`;

const SearchButton = styled.button.attrs({
  className: 'SearchButton'
})`
  padding: 5px 10px;
  border: none;
  background: #9333ea;
  color: white;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    background: #8026c8;
  }
`;

const GiphySection = styled.div.attrs({
  className: 'GiphySection'
})`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GiphyResults = styled.div.attrs({
  className: 'GiphyResults'
})`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
`;

const GiphyItem = styled.div.attrs({
  className: 'GiphyItem'
})`
  width: calc(33.33% - 10px);
  cursor: pointer;

  img {
    width: 100%;
    height: auto;
    border-radius: 3px;
  }
`;

const MediaWrapper = styled.div.attrs({
  className: 'MediaWrapper'
})`
  width: 100%;
  height: auto;
  margin: 10px 0;
`; 